import { AlignType, BorderType, PaddingType } from 'common/types/styleTypes'
import {
  WebsiteButtonTypes,
  WebsiteSettingsTypographyTypeEnum,
} from 'src/shared/types/website-settings-types'
import { useCalculateBorder } from '../use-calculate-border'
import { useCalculatePadding } from '../use-calculate-padding'
import { useCalculateFontFamily } from '../use-calculate-typography/use-calculate-font-family'
import { useCalculateFontSize } from '../use-calculate-typography/use-calculate-font-size'
import { useCalculateFontStyle } from '../use-calculate-typography/use-calculate-font-style'
import { useCalculateFontWeight } from '../use-calculate-typography/use-calculate-font-weight'
import { useCalculateLetterSpacing } from '../use-calculate-typography/use-calculate-letter-spacing'
import { useCalculateLineHeight } from '../use-calculate-typography/use-calculate-line-height'
import { useCalculateTextAlign } from '../use-calculate-typography/use-calculate-text-align'
import {
  UseCalculateButtonHoverProps,
  useCalculateButtonHover,
} from './use-calculate-button-hover'
import {
  UseCalculateButtonThemeColorsProps,
  useCalculateButtonThemeColors,
} from './use-calculate-button-theme-colors'

interface UseCalculateButtonStylesProps
  extends Omit<UseCalculateButtonThemeColorsProps, 'buttonType'>,
    Omit<UseCalculateButtonHoverProps, 'buttonType'> {
  border?: Partial<BorderType>
  mobileBorder?: Partial<BorderType>
  padding?: Partial<PaddingType>
  mobilePadding?: Partial<PaddingType>
  buttonType?: WebsiteButtonTypes
  fontSize?: number
  mobileFontSize?: number
  lineHeight?: number
  mobileLineHeight?: number
  textAlign?: AlignType
  mobileTextAlign?: AlignType
  letterSpacing?: number
  mobileLetterSpacing?: number
  fontFamily?: string
  mobileFontFamily?: string
  fontStyle?: string
  mobileFontStyle?: string
  fontWeight?: string
  mobileFontWeight?: string
}

export const useCalculateButtonStyles = ({
  border,
  mobileBorder,
  padding,
  mobilePadding,
  fontFamily,
  mobileFontFamily,
  fontSize,
  mobileFontSize,
  lineHeight,
  mobileLineHeight,
  textAlign,
  mobileTextAlign,
  letterSpacing,
  mobileLetterSpacing,
  fontStyle,
  mobileFontStyle,
  fontWeight,
  mobileFontWeight,
  textColor,
  mobileTextColor,
  background,
  mobileBackground,
  backgroundColor,
  mobileBackgroundColor,
  hover,
  mobileHover,
  buttonType = WebsiteSettingsTypographyTypeEnum.PrimaryButton,
}: UseCalculateButtonStylesProps) => {
  const returnUseCalculatePadding = useCalculatePadding({
    padding,
    mobilePadding,
    type: buttonType,
  })

  const returnUseCalculateBorder = useCalculateBorder({
    border,
    mobileBorder,
    type: buttonType,
  })
  const returnUseCalculateFontFamily = useCalculateFontFamily({
    fontFamily,
    mobileFontFamily,
    typographyType: buttonType,
  })

  const returnUseCalculateFontStyle = useCalculateFontStyle({
    fontStyle,
    mobileFontStyle,
  })

  const returnUseCalculateFontWeight = useCalculateFontWeight({
    fontWeight,
    mobileFontWeight,
  })

  const returnUseCalculateFontSize = useCalculateFontSize({
    fontSize,
    mobileFontSize,
    typographyType: buttonType,
  })

  const returnUseCalculateLineHeight = useCalculateLineHeight({
    fontSize,
    mobileFontSize,
    lineHeight,
    mobileLineHeight,
    typographyType: buttonType,
  })

  const returnUseCalculateTextAlign = useCalculateTextAlign({
    textAlign,
    mobileTextAlign,
    typographyType: buttonType,
  })

  const returnUseCalculateLetterSpacing = useCalculateLetterSpacing({
    letterSpacing,
    mobileLetterSpacing,
    typographyType: buttonType,
  })

  const returnUseCalculateButtonThemeColors = useCalculateButtonThemeColors({
    textColor,
    mobileTextColor,
    background,
    mobileBackground,
    backgroundColor,
    mobileBackgroundColor,
    buttonType,
  })

  const returnUseCalculateButtonHover = useCalculateButtonHover({
    hover,
    mobileHover,
    buttonType,
  })

  return {
    ...returnUseCalculatePadding,
    ...returnUseCalculateBorder,
    ...returnUseCalculateFontSize,
    calculatedFontSize: {
      mobile: returnUseCalculateFontSize.calculatedFontSize.mobile as number,
      desktop: returnUseCalculateFontSize.calculatedFontSize.desktop as number,
    },
    ...returnUseCalculateLineHeight,
    ...returnUseCalculateTextAlign,
    ...returnUseCalculateLetterSpacing,
    ...returnUseCalculateFontFamily,
    ...returnUseCalculateFontWeight,
    ...returnUseCalculateFontStyle,
    ...returnUseCalculateButtonThemeColors,
    ...returnUseCalculateButtonHover,
  }
}
